export const PATTERN_LENSES = [
  { key: "behavior", label: "Behavior" },
  { key: "timing", label: "Timing" },
  { key: "trends", label: "Trends" },
] as const;

export type PatternLens = (typeof PATTERN_LENSES)[number]["key"];
export type CanonicalPatternType = PatternLens | "silence" | "general";

export interface VerifiedPatternLensEvidence {
  version: 1;
  lens: PatternLens;
  sourceEntryIds: number[];
  repeatedOccurrences?: number;
  sequenceSteps?: number;
  behaviorSequence?: string[];
  minimumDelayHours?: number;
  timingRelationship?: {
    trigger: string;
    outcome: string;
  };
  comparedPeriods?: number;
  periodChangePercent?: number;
  metric?: {
    field: "tag" | "mood" | "secondaryMood" | "timeOfDay";
    value: string;
  };
}

const PATTERN_LENS_BY_TYPE: Readonly<Record<string, PatternLens>> = {
  behavior: "behavior",
  behavioral: "behavior",
  timing: "timing",
  lagged: "timing",
  trends: "trends",
  drift: "trends",
};

export function getPatternLens(patternType: unknown): PatternLens | null {
  if (typeof patternType !== "string") return null;
  return PATTERN_LENS_BY_TYPE[patternType.trim().toLowerCase()] ?? null;
}

export function normalizePatternTypeForStorage(patternType: unknown): CanonicalPatternType {
  const lens = getPatternLens(patternType);
  if (lens) return lens;

  if (typeof patternType === "string" && patternType.trim().toLowerCase() === "silence") {
    return "silence";
  }

  return "general";
}

export interface HomePatternCardInput {
  id: number | string;
  patternType?: unknown;
  headline?: unknown;
  insight?: unknown;
  dataPoints?: unknown;
  lensEvidence?: unknown;
  lensQualified?: unknown;
}

export interface HomePatternTab<T extends HomePatternCardInput> {
  lens: PatternLens;
  label: (typeof PATTERN_LENSES)[number]["label"];
  card: T;
}

function isNonEmptyText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function isVerifiedPatternLensEvidence(
  lens: PatternLens,
  evidence: unknown,
): evidence is VerifiedPatternLensEvidence {
  if (!evidence || typeof evidence !== "object") return false;
  const candidate = evidence as Partial<VerifiedPatternLensEvidence>;
  if (
    candidate.version !== 1
    || candidate.lens !== lens
    || !Array.isArray(candidate.sourceEntryIds)
    || candidate.sourceEntryIds.some(id => !Number.isInteger(id))
  ) {
    return false;
  }

  if (lens === "behavior") {
    return candidate.sourceEntryIds.length >= 4
      && (candidate.repeatedOccurrences ?? 0) >= 2
      && (candidate.sequenceSteps ?? 0) >= 2
      && Array.isArray(candidate.behaviorSequence)
      && candidate.behaviorSequence.length >= 2;
  }
  if (lens === "timing") {
    return candidate.sourceEntryIds.length >= 4
      && (candidate.repeatedOccurrences ?? 0) >= 2
      && (candidate.minimumDelayHours ?? 0) >= 1
      && Boolean(
        candidate.timingRelationship
        && isNonEmptyText(candidate.timingRelationship.trigger)
        && isNonEmptyText(candidate.timingRelationship.outcome),
      );
  }

  return candidate.sourceEntryIds.length >= 4
    && (candidate.comparedPeriods ?? 0) >= 2
    && Math.abs(candidate.periodChangePercent ?? 0) >= 30
    && Boolean(candidate.metric && isNonEmptyText(candidate.metric.value));
}

export function hasHomePatternEvidence(card: HomePatternCardInput): boolean {
  const evidencePoints = Array.isArray(card.dataPoints)
    ? card.dataPoints.filter(isNonEmptyText)
    : [];
  const lens = getPatternLens(card.patternType);
  const hasDurableQualification = lens !== null && (
    card.lensQualified === true
    && isVerifiedPatternLensEvidence(lens, card.lensEvidence)
  );

  return hasDurableQualification
    && isNonEmptyText(card.headline)
    && isNonEmptyText(card.insight)
    && evidencePoints.length >= 2;
}

export function buildHomePatternTabs<T extends HomePatternCardInput>(
  cards: readonly T[],
): HomePatternTab<T>[] {
  const cardsByLens = new Map<PatternLens, T>();

  for (const card of cards) {
    const lens = getPatternLens(card.patternType);
    if (!lens || cardsByLens.has(lens) || !isNonEmptyText(card.headline)) continue;
    cardsByLens.set(lens, card);
  }

  return PATTERN_LENSES.flatMap(({ key, label }) => {
    const card = cardsByLens.get(key);
    return card ? [{ lens: key, label, card }] : [];
  });
}

export function resolveHomePatternTab<T extends HomePatternCardInput>(
  tabs: readonly HomePatternTab<T>[],
  selectedLens: PatternLens | null | undefined,
): HomePatternTab<T> | null {
  return tabs.find(tab => tab.lens === selectedLens) ?? tabs[0] ?? null;
}